import { Form, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { OTPField } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { useIsPending } from '#app/utils/misc.tsx'
import { type ActionFunctionArgs, json, redirect } from '@remix-run/node'
import { z } from 'zod'
import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { post } from '#app/utils/refundr.server.aragorn.ts'
import { getDefaultHomePath } from '#app/utils/home-page.server.ts'
import { commitAuthSession } from '#app/utils/session.server.ts'
import { safeRedirect } from 'remix-utils/safe-redirect'
import React from 'react'
import { type MFAChallengeInput, type SessionResponse } from '#app/model/model.ts'
import { useTranslation, Trans } from 'react-i18next'

export const codeQueryParam = 'code'

export const MFASchema = z.object({
	code: z.string({ required_error: 'Code is required' }).min(6).max(10),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	// This is actually a special session identifier from amazon used for the authenticator app solution, not a refundr session id
	const session = formData.get('session') as string
	const code = formData.get('code') as string
	const email = formData.get('email') as string

	const response = await post<MFAChallengeInput, SessionResponse>(
		request,
		'/aws/mfa-email-challenge/',
		{
			email,
			code,
			session,
		},
		true,
	)

	if (!response.success) {
		if (response.data === 'Invalid session for the user, session is expired.') {
			return json({ customError: 'Invalid session for the user, session is expired. Please login once again.' })
		}
		return json({ customError: response.data })
	}

	if (!response.data?.sessionId) {
		return json({ customError: 'Could not complete sign-in.' })
	}

	return redirect(safeRedirect(response.data.isOnboarded ? getDefaultHomePath(request) : '/onboarding'), {
		headers: {
			'set-cookie': await commitAuthSession(request, {
				sessionId: response.data.sessionId,
				authProvider: 'aws',
				expiresIn: response.data.expiresIn,
			}),
		},
	})
}

export default function MFAEmailChallengeRoute() {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const { t } = useTranslation()
	const actionData = useActionData<typeof action>()

	const [form, fields] = useForm({
		id: 'verify-form',
		constraint: getZodConstraint(MFASchema),
		// @ts-ignore
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: MFASchema })
		},
		defaultValue: {
			code: searchParams.get(codeQueryParam),
		},
	})

	return (
		<div className="flex min-h-screen flex-col md:flex-row">
			{/* Left: Marketing Section */}
			<div className="dark-background order-last flex w-full justify-center border-neutral-200 p-6 text-black md:order-none md:w-1/2 md:border-r dark:border-neutral-800 dark:text-white">
				<div className="my-5 flex flex-col items-center justify-center">
					<div className="pb-8 font-proxima text-neutral-900 dark:text-white">{t('marketing.index.subtitle1')}</div>
					<div>
						<img className="block h-12 w-auto dark:hidden" src="/img/logo/logo-black.png" alt="refundr" />
						<img className="hidden h-12 w-auto dark:block" src="/img/logo/refundr-white.png" alt="refundr" />
					</div>
					<div className="pt-4">
						<img className="block w-auto rounded-xl bg-white shadow-[0_12px_40px_rgba(15,24,42,0.18)] ring-1 ring-neutral-400/30" src="/img/tax-return-card-md.png" alt="refundr" />
					</div>
				</div>
			</div>

			{/* Right: Login Section */}
			<div className="flex w-full items-center justify-center bg-white p-6 md:w-1/2">
				<div className="mt-5 w-full max-w-md bg-white md:-mt-20 lg:relative lg:bottom-10">
					<div className="text-center">
						<h1 className="text-h2 text-gray-600">{t('auth.one-time-code-check-email')}</h1>
						<p className="mt-3 text-body-sm text-muted-foreground"><Trans i18nKey="auth.one-time-code-input" /></p>
					</div>

					<div className="my-2 flex flex-col gap-1 text-center text-sm text-red-500">{actionData?.customError}</div>
					<div className="mx-auto flex w-96 max-w-full flex-col items-center justify-center gap-1">
						<div className="mb-4 flex w-full items-center justify-center gap-2">
							<Form method="POST" {...getFormProps(form)}>
								<HoneypotInputs />
								<OTPField
									labelProps={{
										htmlFor: fields[codeQueryParam].id,
										children: t('auth.one-time-code-label'),
									}}
									inputProps={{
										...getInputProps(fields[codeQueryParam], { type: 'text' }),
										autoComplete: 'one-time-code',
										autoFocus: true,
									}}
									errors={fields[codeQueryParam].errors}
								/>
								<input type="hidden" name="session" value={searchParams.get('session') ?? ''} />
								<input type="hidden" name="email" value={searchParams.get('email') ?? ''} />
								<input type="hidden" name="from" value={searchParams.get('from') ?? ''} />
								<input type="hidden" name="clientId" value={searchParams.get('clientId') ?? ''} />
								<StatusButton
									className="w-full rounded-md bg-neutral-900 dark:bg-blue-600 dark:hover:bg-blue-700 text-white hover:bg-neutral-800"
									status={'idle'}
									type="submit"
									disabled={isPending}
								>
									{t('root.login.submit')}
								</StatusButton>
							</Form>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
